/**
 * Banner when a new service-worker / precache is waiting (PWA update).
 * No-op when the PWA plugin is absent (e.g. some test environments).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';

type UpdateSW = (reloadPage?: boolean) => Promise<void>;

export function PwaUpdateBanner() {
  const { t } = useTranslation();
  const [needRefresh, setNeedRefresh] = useState(false);
  const [updateSW, setUpdateSW] = useState<UpdateSW | null>(null);

  useEffect(() => {
    let cancelled = false;
    void import('virtual:pwa-register')
      .then(({ registerSW }) => {
        if (cancelled) return;
        const update = registerSW({
          immediate: true,
          onNeedRefresh() {
            if (!cancelled) setNeedRefresh(true);
          },
          onOfflineReady() {
            /* shell cached — silent */
          },
        });
        setUpdateSW(() => update);
      })
      .catch(() => {
        /* virtual module unavailable outside Vite PWA builds */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!needRefresh || !updateSW) return null;

  return (
    <div
      role="status"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-linea bg-superficie/95 px-4 py-3 shadow-popover backdrop-blur-sm md:bottom-4 md:left-1/2 md:max-w-md md:-translate-x-1/2 md:rounded-card md:border"
    >
      <p className="text-[13px] font-medium text-tinta">{t('common.pwaUpdate.title')}</p>
      <p className="mt-0.5 text-[12px] text-tinta-2">{t('common.pwaUpdate.body')}</p>
      <div className="mt-3 flex gap-2">
        <Button
          className="flex-1"
          onClick={() => {
            void updateSW(true);
          }}
        >
          {t('common.pwaUpdate.reload')}
        </Button>
        <Button
          variant="ghost"
          className="flex-1"
          onClick={() => setNeedRefresh(false)}
        >
          {t('common.pwaUpdate.dismiss')}
        </Button>
      </div>
    </div>
  );
}
